import { useState, useEffect, useCallback } from 'react'
import api from '../core/services/api'

const AGENT_HEALTH_URL = 'http://127.0.0.1:19847/health'
const AGENT_CHECK_INTERVAL = 30000 // 30s

interface AgentStatus {
  available: boolean
  version?: string
  openDocuments?: number
}

let cachedStatus: AgentStatus = { available: false }
let lastCheck = 0

async function checkAgent(): Promise<AgentStatus> {
  const now = Date.now()
  if (now - lastCheck < 5000) return cachedStatus // debounce 5s

  try {
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 1500)
    const res = await fetch(AGENT_HEALTH_URL, { signal: controller.signal })
    clearTimeout(timeout)

    if (res.ok) {
      const data = await res.json()
      cachedStatus = {
        available: true,
        version: data.version,
        openDocuments: data.open_documents,
      }
    } else {
      cachedStatus = { available: false }
    }
  } catch {
    cachedStatus = { available: false }
  }
  lastCheck = Date.now()
  return cachedStatus
}

export function useDesktopAgent() {
  const [status, setStatus] = useState<AgentStatus>(cachedStatus)

  useEffect(() => {
    checkAgent().then(setStatus)
    const interval = setInterval(() => {
      checkAgent().then(setStatus)
    }, AGENT_CHECK_INTERVAL)
    return () => clearInterval(interval)
  }, [])

  const openWithAgent = useCallback(async (docId: string, docType: string, filename: string): Promise<boolean> => {
    try {
      // 1. Genera token temporaneo
      const res = await api.post('/v1/auth/document-token', { doc_id: docId, doc_type: docType })
      const { token } = res.data

      // 2. Costruisci URL protocol
      const server = window.location.origin
      const url = `maestroerp://open?token=${encodeURIComponent(token)}&doc_id=${encodeURIComponent(docId)}&type=${encodeURIComponent(docType)}&filename=${encodeURIComponent(filename)}&server=${encodeURIComponent(server)}`

      // 3. Invia direttamente all'agent via HTTP (bypassa protocol handler del browser)
      const agentRes = await fetch('http://127.0.0.1:19847/open', {
        method: 'POST',
        body: url,
        signal: AbortSignal.timeout(5000),
      })
      return agentRes.ok
    } catch (err) {
      console.error('openWithAgent failed:', err)
      return false
    }
  }, [])

  const refreshStatus = useCallback(async () => {
    lastCheck = 0
    const s = await checkAgent()
    setStatus(s)
    return s
  }, [])

  return { ...status, openWithAgent, refreshStatus }
}

export function getAgentDownloadUrl(platform: 'windows' | 'mac'): string {
  const base = window.location.origin
  if (platform === 'windows') {
    return `${base}/api/v1/agent/download/windows`
  }
  return `${base}/api/v1/agent/download/mac`
}
